import { Head } from '@inertiajs/react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminArcadeGamesIndex } from '@/types';
import { ArcadeGameFilters } from './components/ArcadeGameFilters';
import { ArcadeGameTable } from './components/ArcadeGameTable';

export default function Index({
  title,
  eventSlug,
  rollout,
  overview,
  games,
  voteCounts,
  pagination,
  filters,
  diagnostics,
}: AdminArcadeGamesIndex) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div>
          <h1 className="font-bold text-3xl tracking-tight">Arcade Games</h1>
          <p className="text-muted-foreground">
            Review the active `/26/arcade` challenge, releases, and rollout
            state.
          </p>
          {rollout && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant={rollout.votingOpen ? 'default' : 'secondary'}>
                {rollout.stateLabel}
              </Badge>
              <span className="text-muted-foreground text-sm">
                Voting deadline:{' '}
                {new Date(rollout.votingDeadline).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
            </div>
          )}
        </div>

        {overview && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Games</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.totalGames}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Versions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.totalVersions}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Validated Plays</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.totalValidatedPlays}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Votes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.totalVotes}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Release Successes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.successfulReleases}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Release Failures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-semibold text-2xl">
                  {overview.failedReleases}
                </div>
                <p className="mt-1 text-muted-foreground text-xs">
                  {overview.latestReleaseAt
                    ? `Latest activity ${new Date(
                        overview.latestReleaseAt,
                      ).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}`
                    : 'No release diagnostics yet'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <ArcadeGameFilters eventSlug={eventSlug} initialParams={filters} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <ArcadeGameTable
              eventSlug={eventSlug}
              games={games}
              pagination={pagination}
              voteCounts={voteCounts}
            />
          </CardContent>
        </Card>

        {rollout && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Release Diagnostics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {diagnostics.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No release diagnostics have been recorded for this challenge
                  yet.
                </p>
              ) : (
                diagnostics.map((diagnostic) => (
                  <div
                    key={diagnostic.id}
                    className="rounded-lg border p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={
                          diagnostic.status === 'succeeded'
                            ? 'default'
                            : 'destructive'
                        }
                      >
                        {diagnostic.status}
                      </Badge>
                      <Badge variant="outline">{diagnostic.stage}</Badge>
                      <span className="font-medium">
                        {diagnostic.githubUsername}/{diagnostic.repoName}
                      </span>
                      <span className="text-muted-foreground">
                        tag {diagnostic.tag}
                      </span>
                      <span className="text-muted-foreground">
                        {new Date(diagnostic.createdAt).toLocaleString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          },
                        )}
                      </span>
                    </div>
                    <p className="mt-2">{diagnostic.message}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
